import { useEffect, useState } from "react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { Panel, PanelHeader } from "@/components/ui/panel";
import { PageSkeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/status/status-badge";
import { api } from "@/lib/api";
import { useOrg } from "@/lib/org-context";

type ActivityItem = {
  id: string;
  title: string;
  detail?: string;
  status: string;
  conferenceId?: string;
  conferenceName?: string;
  destinationProvider?: string;
  createdAt: number;
  remediation?: { title: string; action: string } | null;
};

export function ActivityPanel({ showTitle = false }: { showTitle?: boolean }) {
  const { orgId } = useOrg();
  const [items, setItems] = useState<ActivityItem[]>([]);
  const [status, setStatus] = useState<string>("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const q = status !== "all" ? `&status=${encodeURIComponent(status)}` : "";
    void api<{ items: ActivityItem[] }>(
      `/api/accounts/${orgId}/activity?limit=50${q}`,
    )
      .then((r) => setItems(r.items ?? []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [orgId, status]);

  if (loading) return <PageSkeleton />;

  return (
    <div className="space-y-5">
      {showTitle ? (
        <div>
          <h2 className="text-xl font-semibold text-rs-ink">Activity</h2>
          <p className="text-sm text-rs-ink-muted">
            Recent sync activity across conferences and destinations.
          </p>
        </div>
      ) : (
        <p className="text-sm text-rs-ink-muted">
          Recent sync activity across conferences and destinations.
        </p>
      )}
      <div className="flex flex-wrap gap-1">
        {["all", "succeeded", "failed", "running"].map((s) => (
          <Button
            key={s}
            size="sm"
            variant={status === s ? "primary" : "secondary"}
            onClick={() => setStatus(s)}
          >
            {s}
          </Button>
        ))}
      </div>
      <Panel>
        <PanelHeader title="Timeline" />
        <ul className="divide-y divide-rs-border">
          {items.map((item) => (
            <li key={item.id} className="px-5 py-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="font-medium text-rs-ink">{item.title}</p>
                  <p className="text-sm text-rs-ink-muted">
                    {item.conferenceName ? (
                      <Link
                        className="hover:underline"
                        to={`/conferences/${item.conferenceId}`}
                      >
                        {item.conferenceName}
                      </Link>
                    ) : null}
                    {item.destinationProvider
                      ? ` · ${item.destinationProvider}`
                      : null}
                  </p>
                  {item.detail ? (
                    <p className="mt-1 text-sm text-rs-ink-muted">
                      {item.detail}
                    </p>
                  ) : null}
                  {item.remediation ? (
                    <p className="mt-1 text-sm text-rs-warning">
                      {item.remediation.title}: {item.remediation.action}
                    </p>
                  ) : null}
                </div>
                <StatusBadge
                  tone={
                    item.status === "failed"
                      ? "failed"
                      : item.status.includes("succeed")
                        ? "synced"
                        : "syncing"
                  }
                >
                  {item.status}
                </StatusBadge>
              </div>
            </li>
          ))}
          {items.length === 0 ? (
            <li className="px-5 py-10 text-center text-sm text-rs-ink-muted">
              No activity yet for this organization.
            </li>
          ) : null}
        </ul>
      </Panel>
    </div>
  );
}

/** Standalone Activity view (legacy export). Prefer Reports → Activity tab. */
export function ActivityPage() {
  return <ActivityPanel showTitle />;
}
